import React, { useEffect, useState } from 'react';
import { caseApiService } from '../services/caseApiService';
import { useTheme } from '../contexts/ThemeContext';
import {
    formatProDataReasons,
    GENERIC_PRO_DATA_SUMMARY,
    needsCustodyChoice,
    nonSplitCustodyOptions,
    shouldWarnAboutDivision,
    type NonSplitCustodyArrangement,
    type ProDataReason,
} from '../utils/proDataReasons';

/**
 * ProDowngrade: reads the Pro-only data a case holds from the server. `reasons` is undefined while
 * loading and null when the read failed (callers then show generic wording).
 */
export function useProDataReasons(caseId: string | null | undefined, enabled: boolean): {
    reasons: ProDataReason[] | null | undefined;
    loading: boolean;
} {
    const [reasons, setReasons] = useState<ProDataReason[] | null | undefined>(undefined);

    useEffect(() => {
        if (!enabled || !caseId) {
            setReasons(undefined);
            return;
        }
        let cancelled = false;
        setReasons(undefined);
        caseApiService.getProDataReasons(caseId).then((result) => {
            if (!cancelled) setReasons(result);
        });
        return () => { cancelled = true; };
    }, [caseId, enabled]);

    return { reasons, loading: enabled && !!caseId && reasons === undefined };
}

interface ProDataReasonsNoticeProps {
    /** The case to explain. Nothing is fetched while null or while `enabled` is false. */
    caseId: string | null | undefined;
    enabled?: boolean;
    /** The sentence above the list, e.g. "The copy will not include:". */
    intro: string;
    /** Text size; the copy warning box uses the small size. */
    fontSize?: string;
    /**
     * When given, a case that is actually split asks which non-split arrangement the copy should
     * use instead. The parent sends the choice with the copy request.
     */
    custodyChoice?: NonSplitCustodyArrangement | null;
    onCustodyChoiceChange?: (choice: NonSplitCustodyArrangement) => void;
    /** First names for the custody choices ("Alex has Primary"). */
    partyNames?: { partyA?: string | null; partyB?: string | null };
    /** Tells the parent whether a custody choice is required, so it can hold the copy until one is made. */
    onNeedsCustodyChoiceChange?: (needed: boolean) => void;
}

/**
 * The plain-language list of the Pro-only data in a case: why it is read-only for a non-Pro team,
 * and exactly what a copy will not include. The list comes from the same server walker as the lock.
 */
export const ProDataReasonsNotice: React.FC<ProDataReasonsNoticeProps> = ({
    caseId,
    enabled = true,
    intro,
    fontSize,
    custodyChoice,
    onCustodyChoiceChange,
    partyNames,
    onNeedsCustodyChoiceChange,
}) => {
    const theme = useTheme();
    const { reasons, loading } = useProDataReasons(caseId, enabled);
    const lines = formatProDataReasons(reasons);
    const askCustody = !!onCustodyChoiceChange && needsCustodyChoice(reasons);
    const textStyle: React.CSSProperties = { margin: 0, color: theme.colors.text.primary, fontSize };

    useEffect(() => {
        onNeedsCustodyChoiceChange?.(askCustody);
    }, [askCustody, onNeedsCustodyChoiceChange]);

    return (
        <div data-testid="pro-data-reasons" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
            <p style={textStyle}>{intro}</p>
            {loading ? (
                <p style={{ ...textStyle, color: theme.colors.text.secondary }}>Checking which Pro features this case uses…</p>
            ) : lines.length > 0 ? (
                <ul data-testid="pro-data-reasons-list" style={{ margin: 0, paddingLeft: '1.25em', ...textStyle }}>
                    {lines.map((line) => <li key={line}>{line}</li>)}
                </ul>
            ) : (
                <p style={textStyle}>{capitalize(GENERIC_PRO_DATA_SUMMARY)}.</p>
            )}
            {!loading && shouldWarnAboutDivision(reasons) && (
                <p style={{ ...textStyle, color: theme.colors.text.secondary }}>
                    Because excluded amounts are removed, the copy can show assets as not fully
                    allocated. Review the Division step on the copy before you download documents.
                </p>
            )}
            {askCustody && (
                <div data-testid="pro-data-custody-choice" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs, marginTop: theme.spacing.xs }}>
                    <p style={{ ...textStyle, fontWeight: theme.typography.fontWeight.medium }}>
                        Choose the custody arrangement for the copy:
                    </p>
                    <div role="radiogroup" aria-label="Custody arrangement for the copy" style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.xs }}>
                        {nonSplitCustodyOptions(partyNames).map((option) => {
                            const selected = custodyChoice === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    data-value={option.value}
                                    onClick={() => onCustodyChoiceChange?.(option.value)}
                                    style={{
                                        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                                        borderRadius: theme.borderRadius.sm,
                                        border: `1px solid ${selected ? theme.colors.primary : theme.colors.border.default}`,
                                        backgroundColor: selected ? theme.colors.primary : theme.colors.surface,
                                        color: selected ? theme.colors.text.inverse : theme.colors.text.primary,
                                        fontSize,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
